import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import AdminNav from "../components/AdminNav";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Save, Undo2, LogOut } from "lucide-react";

const TOOL = "que-dibujo";

const AdminCategorias = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/admin/categories/${TOOL}`);
      setItems(res.data.items || []);
      setDefaults(res.data.default || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
      else toast.error("No se pudo cargar.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (items.includes(v)) {
      toast.error("Ya existe.");
      return;
    }
    setItems([...items, v]);
    setDraft("");
  };

  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/categories/${TOOL}`, { items });
      toast.success("Guardado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error.");
    } finally {
      setSaving(false);
    }
  };

  const restore = () => {
    if (!confirm("¿Restaurar categorías por defecto?")) return;
    setItems([...defaults]);
  };

  const logout = () => { localStorage.removeItem("guango_admin_token"); navigate("/admin"); };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-24">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight">categorías.</h1>
          <button onClick={logout} className="press inline-flex items-center gap-2 bg-[#ef4444] text-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm">
            <LogOut className="w-4 h-4" strokeWidth={3} /> salir
          </button>
        </div>
        <p className="font-mono text-sm text-[#4a4a4a] mb-6">
          Categorías de ¿Qué dibujo?. Estas aparecen como botones en la herramienta. Útil para campañas: agrega "Halloween", "Día del padre", "Navidad", etc.
        </p>

        <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-6">
          <div className="flex flex-wrap gap-2 mb-5" data-testid="cat-list">
            {items.map((c, i) => (
              <div
                key={c}
                data-testid={`cat-item-${i}`}
                className="inline-flex items-center gap-2 bg-[#FFB000] border-4 border-[#1a1a1a] rounded-full pl-4 pr-2 py-1.5 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
              >
                <button onClick={() => move(i, -1)} disabled={i === 0} className="font-black disabled:opacity-30">‹</button>
                {c}
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="font-black disabled:opacity-30">›</button>
                <button
                  data-testid={`cat-remove-${i}`}
                  onClick={() => remove(i)}
                  className="ml-1 w-6 h-6 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={3} />
                </button>
              </div>
            ))}
            {items.length === 0 && (
              <div className="font-mono text-sm text-[#737373]">Sin categorías. Agrega al menos una.</div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            <input
              data-testid="cat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="nueva categoría (ej: halloween)"
              className="flex-1 min-w-[200px] bg-[#f7f5f0] border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-sm focus:bg-white"
            />
            <button
              data-testid="cat-add-btn"
              onClick={add}
              className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={3} /> agregar
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="cat-save-btn"
              onClick={save}
              disabled={saving || items.length === 0}
              className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4" strokeWidth={3} /> {saving ? "guardando..." : "guardar todo"}
            </button>
            <button
              onClick={restore}
              className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-5 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm"
            >
              <Undo2 className="w-4 h-4" strokeWidth={3} /> restaurar default
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdminCategorias;
